import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Tag,
  notification as toast,
} from "antd"
import styles from "./styles.module.scss"
import placeholderImage from "assets/placeholder_image.jpg"
import { useTranslation } from "react-i18next"
import { IActivity } from "./types"
import { formatCurrency } from "utils/format-currency"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMemberId } from "pages/member"
import { usePopup } from "utils/use-popup"
import { SESSION_PARAM } from "."
import DisplayEvents from "./displayEvents"
import { useAddActivityEventsToCart } from "./queries"
import { IMember } from "pages/member/types"

interface IActivityItemProps {
  activity: IActivity
  member?: IMember
}

const ShowEvent: React.FC<IActivityItemProps> = ({ activity, member }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const searchParams = useSearchParams()[0]
  const memberId = useMemberId()
  const eventsDrawer = usePopup<{ activityId: string; activityName: string }>()

  const addToCart = useAddActivityEventsToCart(
    { memberId: memberId!, activityId: activity.id },
    {
      meta: {
        type: "add",
        context: "female",
        count: 1,
        entity: t("events.presubscription"),
      },
    }
  )

  const handleAddToCart = () => {
    switch (activity.enrollment) {
      case "activity": {
        if (activity.has_executed_or_closed) {
          toast.error({
            message: t("errors.error"),
            description: t("events.activity_has_executed_or_closed_events"),
            placement: "top",
          })
        } else
          addToCart.mutate({
            session_id: searchParams.get(SESSION_PARAM)!,
          })
        break
      }
      case "event": {
        navigate(
          `show/${activity.id}?${SESSION_PARAM}=${searchParams.get(
            SESSION_PARAM
          )}`
        )
        break
      }
      default:
        break
    }
  }

  return (
    <>
      <Card>
        <Row style={{ marginBottom: 10 }}>
          <Col md={11}>
            <img
              style={{ height: 250, width: "100%" }}
              alt="example"
              src={activity.image ?? placeholderImage}
            />
          </Col>
          <Col md={11} offset={2} style={{ marginBottom: 20 }}>
            <Descriptions
              column={1}
              className={styles.description}
              title={activity.name}
            >
              <Descriptions.Item label={t("date.hours")}>
                {`${activity.start_hour}h - ${activity.end_hour}h`}
              </Descriptions.Item>
              <Descriptions.Item label={t("events.available_places")}>
                {activity.max_participants}
              </Descriptions.Item>
              <Descriptions.Item label={t("events.price")}>
                {activity.is_free ? (
                  <Tag color="success">{t("global.free")}</Tag>
                ) : (
                  `${
                    activity.activity_unit_price_offer
                      ? formatCurrency(activity.activity_unit_price_offer!)
                      : ""
                  } / ${activity.displayed_enrollment}`
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("events.place")}>
                {activity.place?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t("global.description")}>
                {activity.description}
              </Descriptions.Item>
              <Descriptions.Item label={t("global.detail")}>
                {activity.comment}
              </Descriptions.Item>
            </Descriptions>
            <Alert
              style={{ marginTop: 15 }}
              message={t("events.displayed_enrollment_description", {
                field: activity.displayed_enrollment,
              })}
              type="info"
              showIcon
            />
            {activity.member_only && (
              <Alert
                style={{ marginTop: 15 }}
                message={t("global.members_only_acitivity")}
                type="warning"
                showIcon
              />
            )}
            {!activity.subscription && (
              <Alert
                style={{ marginTop: 15 }}
                message={t("global.activity_without_subscription")}
                type="success"
                showIcon
              />
            )}
          </Col>
        </Row>
        <Row gutter={32}>
          <Col>
            <Button
              onClick={handleAddToCart}
              type="primary"
              loading={
                activity.enrollment === "activity" && addToCart.isLoading
              }
              disabled={
                (activity.enrollment === "activity" && addToCart.isLoading) ||
                (activity.member_only &&
                  !(
                    member?.status === "active" &&
                    member?.member_type === "regular"
                  ))
              }
              title={
                activity.member_only &&
                !(
                  member?.status === "active" &&
                  member?.member_type === "regular"
                )
                  ? t("global.members_only_acitivity")
                  : ""
              }
            >
              {t("events.add_to_cart")}
            </Button>
          </Col>
          <Col>
            <Button
              onClick={() =>
                eventsDrawer.open({
                  activityId: activity.id,
                  activityName: activity.name,
                })
              }
              type="primary"
            >
              {t("events.display_events")}
            </Button>
          </Col>
        </Row>
      </Card>
      {eventsDrawer.isOpen && (
        <DisplayEvents
          isOpen={eventsDrawer.isOpen}
          onClose={eventsDrawer.onClose}
          activityId={eventsDrawer.data?.activityId!}
          activityName={eventsDrawer.data?.activityName!}
        />
      )}
    </>
  )
}

export default ShowEvent
